
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useEffect, useMemo } from 'react';
import routes from '../../router/config';

export default function HomePage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Cape Town Painters Platform | Connect with Premium Painting Contractors | Get Up to 5 Quotes';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium platform connecting Cape Town property owners with vetted painting contractors. Get up to 5 detailed quotes from Atlantic Beach Estate, Big Bay, Bloubergstrand, Bothasig, Camps Bay specialists.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Cape Town painters platform, painting contractor quotes, premium painting services, vetted painters Cape Town');
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Cape Town Painters Platform",
      "description": "Premium platform connecting property owners with Cape Town's finest painting contractors. Get up to 5 detailed quotes from vetted professionals.",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "provider": {
        "@type": "Organization",
        "name": "Cape Town Painters",
        "telephone": "+27123456789",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Cape Town",
          "addressRegion": "Western Cape",
          "addressCountry": "ZA"
        }
      },
      "serviceType": "Painting Contractor Connection Platform",
      "areaServed": {
        "@type": "City",
        "name": "Cape Town"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Premium Painting Contractor Network",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Multiple Contractor Quotes",
              "description": "Get up to 5 detailed quotes from vetted painting contractors"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Best-Match Contractor",
              "description": "Connect with our single best-matched contractor for your project"
            }
          }
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "156"
      }
    });
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // Automatically generate area pages from routes
  const serviceAreas = useMemo(() => {
    return routes
      .filter(route => route.path?.startsWith('/areas/'))
      .map(route => {
        const path = route.path!;
        const name = path
          .replace('/areas/', '')
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        // Map area names to appropriate icons
        const getAreaIcon = (areaName) => {
          const lowerName = areaName.toLowerCase();
          if (lowerName.includes('beach') || lowerName.includes('bay')) return 'ri-ship-line';
          if (lowerName.includes('estate')) return 'ri-home-smile-line';
          if (lowerName.includes('point')) return 'ri-anchor-line';
          if (lowerName.includes('view')) return 'ri-eye-line';
          if (lowerName.includes('city')) return 'ri-building-2-line';
          if (lowerName.includes('camps')) return 'ri-mountain-line';
          if (lowerName.includes('clifton')) return 'ri-landscape-line';
          if (lowerName.includes('green')) return 'ri-leaf-line';
          return 'ri-map-pin-line';
        };

        return { 
          name, 
          href: path,
          icon: getAreaIcon(name)
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Residential Interior Painting',
      description: 'Transform your home with our expert residential painting services that bring your vision to life. We specialize in creating beautiful, lasting finishes for every room in your house, from bedrooms and living areas to kitchens and bathrooms.',
      features: ['Interior wall painting with premium finishes', 'Ceiling treatments and texture applications', 'Decorative painting and accent walls', 'Exact color matching and consultation', 'Wallpaper removal and surface preparation', 'Trim and cabinet refinishing']
    },
    {
      icon: 'ri-building-2-line',
      title: 'Exterior House Painting',
      description: 'Protect and enhance your property\'s curb appeal with our comprehensive exterior painting services. We use weather-resistant paints and advanced techniques to ensure your home looks stunning and stays protected against Cape Town\'s coastal elements.',
      features: ['Complete exterior house painting', 'Roof painting and waterproofing', 'Deck and fence staining', 'Pressure washing and surface prep', 'Weather damage restoration', 'Protective coating applications']
    },
    {
      icon: 'ri-community-line',
      title: 'Commercial Painting Services',
      description: 'Professional painting solutions for businesses, offices, retail spaces, and industrial properties. We work efficiently to minimize disruption to your operations while delivering exceptional results that reflect your brand\'s professionalism.',
      features: ['Office buildings and complexes', 'Retail establishments and showrooms', 'Industrial facilities and warehouses', 'Scheduled maintenance programs', 'Interior and exterior commercial work', 'Specialty coatings and finishes']
    }
  ];

  const additionalServices = [
    {
      icon: 'ri-brush-3-line',
      title: 'Specialty Finishes',
      description: 'Custom decorative painting techniques including faux finishes, texture applications, and artistic wall treatments that add character and sophistication to any space.'
    },
    {
      icon: 'ri-hammer-line',
      title: 'Surface Preparation',
      description: 'Comprehensive preparation services including crack repair, surface cleaning, priming, and ensuring optimal paint adhesion for long-lasting results.'
    },
    {
      icon: 'ri-droplet-line',
      title: 'Waterproofing Services',
      description: 'Professional waterproofing solutions for roofs, walls, and foundations to protect your property from moisture damage and weather exposure.'
    },
    {
      icon: 'ri-palette-line',
      title: 'Color Consultation',
      description: 'Expert color consultation services to help you choose the perfect palette that complements your style, lighting, and architectural features.'
    }
  ];

  const testimonials = [
    {
      name: 'Jennifer Thompson',
      location: 'Atlantic Beach Estate',
      rating: 5,
      text: 'Outstanding service from start to finish! The team was incredibly professional, arriving on time every day and completing the work with exceptional attention to detail. The quality exceeded all expectations and transformed our home beautifully.',
      image: 'https://painters-capetown.co.za/wp-content/uploads/2020/03/painting-attention-to-detail.jpg'
    },
    {
      name: 'David Rodriguez',
      location: 'Camps Bay',
      rating: 5,
      text: 'Exceptional craftsmanship and attention to detail throughout the entire project. They transformed our entire home with premium finishes and completed everything ahead of schedule. Highly recommend their services.',
      image: 'https://painters-capetown.co.za/wp-content/uploads/2020/03/painting-surface-preparation.jpg'
    },
    {
      name: 'Amanda Foster',
      location: 'Big Bay',
      rating: 5,
      text: 'Professional, reliable, and delivered exactly what was promised. The quality of their workmanship is simply outstanding and the final result exceeded our expectations. Will definitely use their services again.',
      image: 'https://painters-capetown.co.za/wp-content/uploads/2020/03/highest-quality-painting-services.jpg'
    }
  ];

  const projects = [
    {
      title: 'Luxury Coastal Residence',
      location: 'Bloubergstrand',
      description: 'Complete interior and exterior transformation of a luxury beachfront property',
      image: 'https://painters-capetown.co.za/wp-content/uploads/2020/03/interior-painters-cape-town.jpg'
    },
    {
      title: 'Corporate Headquarters Renovation',
      location: 'Cape Town CBD',
      description: 'Professional commercial painting for a multi-story office complex',
      image: 'https://painters-capetown.co.za/wp-content/uploads/2020/03/exterior-painters-cape-town.jpg'
    },
    {
      title: 'Heritage Property Restoration',
      location: 'Bothasig',
      description: 'Careful restoration and painting of a historic residential property',
      image: 'https://painters-capetown.co.za/wp-content/uploads/2020/03/roof-painters-cape-town.jpg'
    }
  ];

  const whyChooseUs = [
    {
      icon: 'ri-team-line',
      title: 'Vetted Contractor Network',
      description: 'Access Cape Town\'s most skilled painting contractors through our carefully curated network. Every contractor is background-checked, licensed, and committed to excellence in their craft.'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Up to 5 Detailed Quotes',
      description: 'Compare comprehensive quotes from multiple premium contractors, or choose our streamlined single best-match option. Make informed decisions with transparent pricing and detailed proposals.'
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Premium Quality Focus',
      description: 'We don\'t compete on price – we compete on quality. Our contractors use top-tier materials from leading manufacturers to ensure exceptional durability and beautiful finishes.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Assurance',
      description: 'Complete peace of mind with our comprehensive professional commitment and satisfaction promise that protects your investment throughout every phase of the project.'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Local Expertise',
      description: 'Specialists who understand Cape Town\'s unique coastal challenges. From marine-grade protection for beachfront properties to wind-resistant coatings for exposed areas.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Personalized Matching',
      description: 'Our platform intelligently matches you with contractors based on your specific project requirements, location, timeline, and quality expectations for optimal results.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/41ccb8320f976a8f93deade783460772.jpg"
            alt="Professional painting contractors working on modern house exterior in Cape Town"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="space-y-8">
              <div className="space-y-6">
                <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                  Connect with Cape Town's <span className="text-yellow-400">Premium Painters</span>
                </h1>
                <p className="text-xl text-gray-300 leading-relaxed">
                  Get up to 5 detailed quotes from our vetted network of Cape Town's finest painting contractors. Serving Atlantic Beach Estate, Big Bay, Bloubergstrand, Bothasig, Camps Bay with quality-focused professionals.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  Get Free Quote
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-6 pt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">5</div>
                  <div className="text-gray-300">Quotes Available</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">500+</div>
                  <div className="text-gray-300">Projects Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">100%</div>
                  <div className="text-gray-300">Vetted Contractors</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-yellow-400/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-yellow-400/10 rounded-full blur-xl"></div>
      </section>

      {/* Platform Introduction Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
                Cape Town's Premium Painting Platform
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Cape Town Painters is a premium platform that connects discerning property owners with the finest painting contractors in Cape Town. We understand that finding the right painter for your valuable property can be challenging, which is why we've created a curated network of only the most skilled and reliable professionals.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our platform allows you to request up to 5 detailed quotes from our vetted painting contractors, giving you the power to compare services, expertise, and offerings. Alternatively, if you prefer a streamlined approach, we can connect you with our single best-matched contractor based on your specific requirements.
              </p>
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-yellow-500 mb-2">5</div>
                  <div className="text-gray-600 font-medium">Quotes Available</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-yellow-500 mb-2">100%</div>
                  <div className="text-gray-600 font-medium">Vetted Network</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Premium%20painting%20contractors%20network%20platform%2C%20professional%20painters%20working%20on%20luxury%20Cape%20Town%20properties%2C%20quality%20painting%20services%2C%20multiple%20contractors%20collaboration%2C%20professional%20service%20platform%2C%20realistic%20photography%20with%20Cape%20Town%20cityscape&width=600&height=600&seq=platform-network-home&orientation=squarish"
                alt="Cape Town Painters premium contractor network platform"
                className="rounded-2xl shadow-2xl object-cover object-top w-full h-[600px]"
              />
              <div className="absolute -bottom-8 -left-8 bg-yellow-400 text-black p-8 rounded-2xl shadow-xl">
                <div className="text-4xl font-bold">Quality</div>
                <div className="text-lg font-medium">Over Price</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              How Our Platform Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Connect with Cape Town's finest painting contractors in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Submit Your Project',
                description: 'Tell us about your painting needs, location, timeline, and preferences. Our intelligent matching system identifies the best contractors for your specific requirements.',
                icon: 'ri-file-text-line'
              },
              {
                step: '02',
                title: 'Receive Quality Quotes',
                description: 'Get up to 5 detailed quotes from our vetted contractor network, or choose our single best-match option. Compare expertise, timelines, and transparent pricing.',
                icon: 'ri-file-list-3-line'
              },
              {
                step: '03',
                title: 'Choose & Connect',
                description: 'Review contractor profiles, previous work, and customer reviews. Select your preferred contractor and work directly with them for premium results.',
                icon: 'ri-hand-heart-line'
              }
            ].map((process, index) => (
              <div key={index} className="bg-white rounded-2xl p-10 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100">
                <div className="w-20 h-20 bg-yellow-400 rounded-2xl flex items-center justify-center mx-auto mb-8" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${process.icon} text-3xl text-black`}></i>
                </div>
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-2xl font-bold text-black mb-6">{process.title}</h3>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Our Professional Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We offer comprehensive painting solutions for residential and commercial properties throughout Cape Town
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-4-line',
                title: 'Interior Painting',
                description: 'Transform your indoor spaces with our expert interior painting services using premium quality paints'
              },
              {
                icon: 'ri-building-line',
                title: 'Exterior Painting',
                description: 'Protect and beautify your property exterior with weather-resistant paints and professional techniques'
              },
              {
                icon: 'ri-community-line',
                title: 'Commercial Painting',
                description: 'Professional painting services for offices, retail spaces, and commercial buildings'
              },
              {
                icon: 'ri-home-8-line',
                title: 'Roof Painting',
                description: 'Extend your roof life and improve energy efficiency with our specialized roof painting services'
              },
              {
                icon: 'ri-drop-line',
                title: 'Waterproofing',
                description: 'Comprehensive waterproofing solutions to protect your property from water damage'
              },
              {
                icon: 'ri-palette-line',
                title: 'Color Consultation',
                description: 'Expert color advice to help you choose the perfect palette for your space'
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-yellow-400 rounded-lg flex items-center justify-center mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
              Why Choose Our Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              We don't compete on price – we compete on quality. Our commitment to connecting you with only the finest contractors has made us the preferred platform for discerning property owners throughout Cape Town.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${item.icon} text-3xl text-black`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Our Proven Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a systematic approach to ensure exceptional results on every project
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                icon: 'ri-phone-line',
                title: 'Free Consultation',
                description: 'We assess your needs and provide detailed recommendations'
              },
              {
                step: '02',
                icon: 'ri-file-text-line',
                title: 'Detailed Quote',
                description: 'Transparent pricing with no hidden costs or surprises'
              },
              {
                step: '03',
                icon: 'ri-tools-line',
                title: 'Professional Work',
                description: 'Expert execution using premium materials and techniques'
              },
              {
                step: '04',
                icon: 'ri-check-line',
                title: 'Quality Assurance',
                description: 'Final inspection to ensure perfect results and your satisfaction'
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${step.icon} text-black text-2xl`}></i>
                </div>
                <div className="text-sm font-bold text-yellow-600 mb-2">{step.step}</div>
                <h3 className="text-xl font-bold text-black mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Showcase of Network Excellence
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Explore projects completed by our vetted contractor network that demonstrate our commitment to connecting you with quality, craftsmanship, and exceptional attention to detail
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-80 object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{project.title}</h3>
                  <p className="text-gray-200 flex items-center mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </p>
                  <p className="text-gray-300 text-sm">{project.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to="/contact"
              className="bg-yellow-400 text-black px-10 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              Connect with Our Network
            </Link>
          </div>
        </div>
      </section>

      {/* Quality Standards Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20professional%20painter%20painting%20exterior%20of%20contemporary%20house%20in%20Camps%20Bay%20with%20Twelve%20Apostles%20mountain%20backdrop%2C%20skilled%20craftsman%20with%20brush%20and%20paint%20equipment%2C%20high%20quality%20workmanship%2C%20sunny%20day%2C%20realistic%20photography%2C%20premium%20painting%20service&width=600&height=500&seq=quality-standards-painter&orientation=portrait"
                alt="Professional painter working on modern Camps Bay house"
                className="rounded-2xl shadow-2xl object-cover object-top w-full h-[500px]"
              />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
                Uncompromising Quality Standards
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Every contractor in our network maintains the highest standards in every aspect of their work, from material selection to final inspection. Our commitment to quality is reflected in every brush stroke and finish they apply.
              </p>
              <div className="space-y-6">
                {[
                  'Premium paint brands and materials only',
                  'Thorough surface preparation and priming',
                  'Skilled craftsmen with years of experience',
                  'Quality control at every stage',
                  'Environmental safety compliance',
                  'Complete satisfaction commitment'
                ].map((standard, index) => (
                  <div key={index} className="flex items-center">
                    <i className="ri-checkbox-circle-line text-2xl text-green-500 mr-4"></i>
                    <span className="text-lg text-gray-700 font-medium">{standard}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              What Our Platform Users Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from property owners who found their perfect painting contractor through our platform
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-10 relative shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                <div className="flex items-center mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                <blockquote className="text-gray-700 text-lg mb-8 italic leading-relaxed">
                  "{testimonial.text}"
                </blockquote>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover object-top mr-4"
                  />
                  <div>
                    <h4 className="font-bold text-black text-lg">{testimonial.name}</h4>
                    <p className="text-gray-600">{testimonial.location}</p>
                  </div>
                </div>
                <div className="absolute top-8 right-8">
                  <i className="ri-double-quotes-r text-4xl text-yellow-200"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Service Areas</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We proudly serve Cape Town and surrounding areas with professional painting services
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {serviceAreas.map((area, index) => (
              <Link
                key={index}
                to={area.href}
                className="bg-white rounded-lg p-6 text-center hover:shadow-lg transition-shadow cursor-pointer group"
              >
                <div className="w-12 h-12 bg-yellow-400 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-yellow-500 transition-colors" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${area.icon} text-black text-xl`}></i>
                </div>
                <h3 className="font-semibold text-black group-hover:text-yellow-600 transition-colors">{area.name}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-r from-yellow-400 to-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
            Ready to Connect with Premium Painters?
          </h2>
          <p className="text-xl text-black/80 mb-12 max-w-4xl mx-auto leading-relaxed">
            Experience the difference that our premium platform makes. Connect with Cape Town's most trusted painting contractors today and discover why discerning property owners choose our curated network for their most important projects.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Elfsight Popup */}
      <div className="elfsight-app-a8d09b4b-1f9c-47af-9c95-257e6e1aa6a7" data-elfsight-app-lazy></div>
    </div>
  );
}
